import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useReportsStore } from '@/stores/reports';
import { ERouterNames, INavigationItemPlus } from '@/types';

/**
 * Hook that determines the active navigation item based on the current URL pathname.
 * Returns the active item's key and a function to manually set it.
 * @param availableItems - Array of navigation items, including main items and their sub-items.
 * @returns An object containing the activeKey (string or undefined) and setActiveKey function.
 */
export const useActiveNavigation = (availableItems: INavigationItemPlus[]) => {
  const location = useLocation();
  const [activeKey, setActiveKey] = useState<string | undefined>(undefined);
  const { reportConfig } = useReportsStore((state) => ({
    reportConfig: state.reportConfig,
  }));

  /**
   * Function to find the active navigation item's key based on the current pathname.
   * Compares the pathname against routes in availableItems and reportConfig.
   */
  const findActiveKey = useMemo(() => {
    return (pathname: string) => {
      /**
       * Iterates through all navigation items to check sub-items (if any) for a matching route.
       * Returns the key of the sub-item whose route matches the current pathname.
       */
      for (const item of availableItems) {
        if (item.subItems) {
          /**
           * Iterates through sub-items of the current navigation item.
           * Checks if the pathname matches the sub-item's route (ignoring query parameters)
           * or if the pathname starts with the sub-item's route (for nested routes).
           */
          for (const subItem of item.subItems) {
            const routeWithoutQuery = subItem.route.split('?')[0];
            if (
              pathname === routeWithoutQuery ||
              pathname.startsWith(`${routeWithoutQuery}/`)
            ) {
              return subItem.key;
            }
          }
        }
      }

      /**
       * Iterates through all navigation items to check their main routes.
       * Returns the key of the item whose route matches the current pathname
       * (ignoring query parameters) or starts with the route (for nested routes).
       */
      for (const item of availableItems) {
        const routeWithoutQuery = item.route.split('?')[0];
        if (
          pathname === routeWithoutQuery ||
          pathname.startsWith(`${routeWithoutQuery}/`)
        ) {
          return item.key;
        }
      }

      /**
       * Iterates through report configurations (if available) to check for non-visible reports
       * with an 'extends' property. Returns the last key in the 'extends' array if the pathname
       * matches the dynamically constructed report route or starts with it.
       */
      if (reportConfig) {
        for (const report of reportConfig) {
          if (!report.visible && report.extends && report.extends.length > 0) {
            const routeWithoutQuery = `${ERouterNames.DATA_REPORTING}/${report.id}`;
            if (
              pathname === routeWithoutQuery ||
              pathname.startsWith(`${routeWithoutQuery}/`)
            ) {
              return report.extends.slice(-1)[0];
            }
          }
        }
      }

      return undefined;
    };
  }, [availableItems, reportConfig]);

  useEffect(() => {
    const matchKey = findActiveKey(location.pathname);
    setActiveKey(matchKey);
  }, [findActiveKey, location.pathname]);

  return {
    activeKey,
    setActiveKey,
  };
};
